import { todayStr } from "../../lib/date";
import React, { useState } from "react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Button } from "../ui/button";
import { updateStudentSchedule } from "../../lib/api";
import ScheduleReasonFields, { EMPTY_REASON } from "./ScheduleReasonFields";
import { BeforeAfterWeekly } from "./ScheduleChangeHistory";

const DEFAULT_SCHEDULE = {
  monday: { expected: true, start: "08:30", end: "14:00" },
  tuesday: { expected: true, start: "08:30", end: "14:00" },
  wednesday: { expected: true, start: "08:30", end: "14:00" },
  thursday: { expected: true, start: "08:30", end: "14:00" },
  friday: { expected: true, start: "08:30", end: "12:00" },
};
const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday"];

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(String(iso).slice(0, 10) + "T00:00:00");
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

/**
 * The Edit-Schedule form body (effective date + weekday editor + before/after
 * preview + reason fields). Shared by the student-file Schedule tab and the
 * school-wide Schedule Changes page. Renders the inner content only — wrap it in
 * a Dialog. Calls `updateStudentSchedule`, so emails + logging stay identical.
 */
export default function ScheduleChangeForm({ student, studentId, staff = [], onSaved, onCancel }) {
  const base = student?.schedule && Object.keys(student.schedule).length > 0 ? student.schedule : DEFAULT_SCHEDULE;
  const [editingSchedule, setEditingSchedule] = useState(() => {
    const seed = {};
    WEEKDAYS.forEach((d) => { seed[d] = { ...DEFAULT_SCHEDULE[d], ...(base[d] || {}) }; });
    return { ...seed, _effective_date: todayStr(), ...EMPTY_REASON };
  });
  const [saving, setSaving] = useState(false);

  const previewAfter = (() => {
    const out = {};
    WEEKDAYS.forEach((d) => { out[d] = { ...DEFAULT_SCHEDULE[d], ...(editingSchedule[d] || {}) }; });
    return out;
  })();

  const handleSave = async () => {
    if (!editingSchedule.reason_category) {
      toast.error("Please choose a reason for this change");
      return;
    }
    setSaving(true);
    try {
      const effectiveDate = editingSchedule._effective_date || todayStr();
      const payload = {
        schedule: previewAfter,
        effective_date: effectiveDate,
        reason_category: editingSchedule.reason_category,
        reason_notes: editingSchedule.reason_notes,
        approved_by_id: editingSchedule.approved_by_id,
        approved_by_name: editingSchedule.approved_by_name,
        parent_notified: !!editingSchedule.parent_notified,
        parent_notified_date: editingSchedule.parent_notified_date,
      };
      const res = await updateStudentSchedule(studentId, payload);
      if (res.data?.scheduled) {
        toast.success(`Change scheduled for ${fmtDate(res.data.effective_date)} — current schedule unchanged until then`);
      } else {
        toast.success("Schedule updated");
      }
      onSaved && onSaved({ ...(res.data || {}), appliedSchedule: previewAfter });
    } catch {
      toast.error("Failed to save schedule change");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 pt-2">
      <div className="space-y-2">
        <Label>Effective Date <span className="text-xs text-muted-foreground">(a future date is scheduled and applied automatically that day)</span></Label>
        <Input
          type="date"
          value={editingSchedule._effective_date || todayStr()}
          onChange={(e) => setEditingSchedule((p) => ({ ...p, _effective_date: e.target.value }))}
          data-testid="schedule-effective-date"
        />
      </div>
      {WEEKDAYS.map((day) => (
        <div key={day} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border">
          <div className="flex items-center space-x-2 min-w-[140px]">
            <Checkbox
              id={`sched-${day}`}
              checked={editingSchedule[day]?.expected ?? true}
              onCheckedChange={(checked) => setEditingSchedule((p) => ({ ...p, [day]: { ...p[day], expected: checked } }))}
            />
            <Label htmlFor={`sched-${day}`} className="capitalize font-medium">{day}</Label>
          </div>
          <div className="flex flex-wrap items-center gap-2 flex-1">
            <Label className="text-sm text-muted-foreground">Start:</Label>
            <Input type="time" value={editingSchedule[day]?.start || "08:30"}
              onChange={(e) => setEditingSchedule((p) => ({ ...p, [day]: { ...p[day], start: e.target.value } }))}
              className="w-28 sm:w-32" disabled={!editingSchedule[day]?.expected} />
            <Label className="text-sm text-muted-foreground">End:</Label>
            <Input type="time" value={editingSchedule[day]?.end || (day === "friday" ? "12:00" : "14:00")}
              onChange={(e) => setEditingSchedule((p) => ({ ...p, [day]: { ...p[day], end: e.target.value } }))}
              className="w-28 sm:w-32" disabled={!editingSchedule[day]?.expected} />
          </div>
        </div>
      ))}

      <div className="rounded-lg border bg-blue-50/40 p-3" data-testid="schedule-before-after-preview">
        <p className="text-xs font-semibold text-gray-600 mb-2">Preview of this change</p>
        <BeforeAfterWeekly before={base} after={previewAfter} />
      </div>

      <ScheduleReasonFields form={editingSchedule} setForm={setEditingSchedule} staff={staff} testidPrefix="sched-" />

      <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="save-schedule-btn">
          {saving ? "Saving…" : "Save Change"}
        </Button>
      </div>
    </div>
  );
}
